import { useEffect } from "react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";

const HEADLINE = {
  held: "is not answering — tickets are held",
  rerouted: "is not answering — tickets went to its backup",
  hard_fail: "could not print a ticket",
};

export const MAX_ALERT_COUNT = 3;

/**
 * The business day a moment belongs to, as YYYY-MM-DD: the day rolls over at the
 * outlet's Day Change Time (business_day_start, default 06:00), not at midnight,
 * the same rule the server's order_date follows.
 */
export function businessDay(now, dayStart = "06:00") {
  const [h, m] = String(dayStart || "06:00").split(":").map(Number);
  const shifted = new Date(now - ((Number.isFinite(h) ? h : 6) * 60 + (Number.isFinite(m) ? m : 0)) * 60000);
  const pad = n => String(n).padStart(2, "0");
  return `${shifted.getFullYear()}-${pad(shifted.getMonth() + 1)}-${pad(shifted.getDate())}`;
}

const alertCounts = new Map();

export function _resetAlertCountsForTest() {
  alertCounts.clear();
  try {
    sessionStorage.clear();
  } catch {}
}

// { count, day } per printer and event, kept in sessionStorage so a reload does
// not start the warnings over.
function getAlertState(key) {
  try {
    const val = sessionStorage.getItem(`printer_alert_count_${key}`);
    if (val !== null) {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed.count === "number") return parsed;
    }
  } catch {}
  return alertCounts.get(key) || { count: 0, day: null };
}

function incrementAlertCount(key, day) {
  const state = getAlertState(key);
  const next = state.day === day ? { count: state.count + 1, day } : { count: 1, day };
  alertCounts.set(key, next);
  try {
    sessionStorage.setItem(`printer_alert_count_${key}`, JSON.stringify(next));
  } catch {}
  return next.count;
}

/**
 * A ticket that will not print now is said on screen, by printer name (operating
 * law 3; owner list 2026-11-01). The server sends `alert` only for tickets held for
 * a dead printer, sent to its backup, or given up on — not for each retry. One toast
 * per printer: a dead printer during service replaces its own warning rather than
 * stacking one per KOT.
 *
 * After 3 warnings for the same printer and event it goes quiet for the rest of the
 * business day (owner, 2026-11-01: "instead of 30 make it 1 business day"), so a
 * dead printer does not flood the till all service; at the Day Change Time it is
 * said again if it is still failing — never silenced for good (operating law 3).
 */
export function usePrinterAlerts() {
  const { subscribeRealtime, settings } = usePOS();
  const dayStart = settings?.business_day_start;
  useEffect(() => {
    if (!subscribeRealtime) return undefined;
    return subscribeRealtime(event => {
      if (event?.type !== "printer_event" || !event.alert) return;

      const key = `${event.printerId ?? "none"}:${event.eventType || "alert"}`;
      const count = incrementAlertCount(key, businessDay(Date.now(), dayStart));
      if (count > MAX_ALERT_COUNT) return;

      const isLast = count === MAX_ALERT_COUNT;
      const description = isLast
        ? (event.text ? `${event.text} (no more warnings for it until the day changes)` : "No more warnings for it until the day changes")
        : event.text;

      toast.error(`${event.printerName || "Printer"} ${HEADLINE[event.eventType] || "has a problem"}`, {
        id: `printer-alert-${event.printerId ?? "none"}`,
        description,
        duration: isLast ? 10000 : 20000,
      });
    });
  }, [subscribeRealtime, dayStart]);
}
